import { useState, useEffect } from 'react';
import { BadgeBuilder } from "../components/admin/BadgeBuilder";
import { CouponGenerator } from "../components/admin/CouponGenerator";
import { BulkBadgeAward } from "../components/admin/BulkBadgeAward";
import ImpersonationPanel from "../components/admin/ImpersonationPanel";
import { adminBadgeService, type BadgeFormData } from "../services/adminBadgeService";
import { useNotification } from "../contexts/NotificationContext";
import axios from '../axios-config';

type AdminTool = 'badges' | 'coupons' | 'bulk-award' | 'manage-badges' | 'dev-tools';

interface BadgeCrudManagerProps {
  badges: BadgeFormData[];
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}

// Move BadgeCrudManager to its own component below
const BadgeCrudManager = ({ badges, onEdit, onDelete }: BadgeCrudManagerProps) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {badges.length === 0 ? (
      <div className="col-span-full text-center text-gray-400 py-12">No badges found.</div>
    ) : (
      badges.map((badge: BadgeFormData, index: number) => (
        <div key={index} className="relative bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="h-12 w-12 flex-shrink-0 rounded-full overflow-hidden" style={{ backgroundColor: `${badge.color}20` }}>
                <img src={`${badge.badgeImageUrl}`} alt={`${badge.name} badge`} className="w-full h-full object-cover" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">{badge.name}</h3>
                <p className="text-sm text-gray-500">{badge.category}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button onClick={() => onEdit(index)} className="p-1 text-gray-400 hover:text-indigo-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a4 4 0 01-2.828 1.172H7v-2a4 4 0 011.172-2.828z" /></svg>
              </button>
              <button onClick={() => onDelete(index)} className="p-1 text-gray-400 hover:text-red-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
            <p className="text-sm text-gray-600 mb-4">{badge.description}</p>
            <div className="space-y-2">
              {badge.levels
                .slice()
                .sort((a, b) => a.threshold - b.threshold)
                .map(level => (
                  <div key={level.name} className="flex justify-between text-sm">
                    <span>{level.name.charAt(0).toUpperCase() + level.name.slice(1)}</span>
                    <span>{level.threshold} XP</span>
                  </div>
                ))}
            </div>
        </div>
      ))
    )}
  </div>
);

// Developer tools for reset DB and logs
const DevTools = () => {
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [serverLogs, setServerLogs] = useState('');
  const [workerLogs, setWorkerLogs] = useState('');
  const [logLoading, setLogLoading] = useState({ server: false, worker: false });
  const [logError, setLogError] = useState<{ server?: string; worker?: string }>({});

  const handleResetDB = async () => {
    setResetLoading(true);
    setResetMessage(null);
    try {
      const res = await axios.post('/api/ResetDB');
      setResetMessage(res.data?.message || 'Database reset complete');
    } catch (err: any) {
      setResetMessage(err.message || 'Error resetting database');
    } finally {
      setResetLoading(false);
    }
  };

  const handleGetServerLogs = async () => {
    setLogLoading(prev => ({ ...prev, server: true }));
    setLogError(prev => ({ ...prev, server: undefined }));
    try {
      const res = await axios.get<string>('/api/Logs/server');
      setServerLogs(res.data);
    } catch (err: any) {
      setLogError(prev => ({ ...prev, server: err.message }));
    } finally {
      setLogLoading(prev => ({ ...prev, server: false }));
    }
  };

  const handleGetWorkerLogs = async () => {
    setLogLoading(prev => ({ ...prev, worker: true }));
    setLogError(prev => ({ ...prev, worker: undefined }));
    try {
      const res = await axios.get<string>('/api/Logs/worker');
      setWorkerLogs(res.data);
    } catch (err: any) {
      setLogError(prev => ({ ...prev, worker: err.message }));
    } finally {
      setLogLoading(prev => ({ ...prev, worker: false }));
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-medium">Developer Tools</h2>
      {/* User Impersonation Panel */}
      <div className="space-y-2">
        <ImpersonationPanel />
      </div>
      {/* Reset Database */}
      <div className="space-y-2">
        <button
          onClick={handleResetDB}
          disabled={resetLoading}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
        >
          {resetLoading ? 'Resetting...' : 'Reset Database'}
        </button>
        {resetMessage && <p className="mt-1 text-gray-700">{resetMessage}</p>}
      </div>
      {/* Server Logs */}
      <div className="space-y-2">
        <button
          onClick={handleGetServerLogs}
          disabled={logLoading.server}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {logLoading.server ? 'Loading Server Logs...' : 'Get Server Logs'}
        </button>
        {logError.server && <p className="text-red-500">{logError.server}</p>}
        {serverLogs && <pre className="mt-1 max-h-64 overflow-auto bg-gray-100 p-2">{serverLogs}</pre>}
      </div>
      {/* Worker Logs */}
      <div className="space-y-2">
        <button
          onClick={handleGetWorkerLogs}
          disabled={logLoading.worker}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {logLoading.worker ? 'Loading Worker Logs...' : 'Get Worker Logs'}
        </button>
        {logError.worker && <p className="text-red-500">{logError.worker}</p>}
        {workerLogs && <pre className="mt-1 max-h-64 overflow-auto bg-gray-100 p-2">{workerLogs}</pre>}
      </div>
    </div>
  );
};

interface AdminToolsPageProps {
  initialTool?: AdminTool;
}
// Main Admin Tools Page with tabs: badges, coupons, bulk-award, manage-badges, dev-tools
const AdminToolsPage = ({ initialTool }: AdminToolsPageProps) => {
  const [activeTool, setActiveTool] = useState<AdminTool>(initialTool ?? 'badges');
  const [badgeTab, setBadgeTab] = useState<'create' | 'manage'>('create');
  const [badges, setBadges] = useState<BadgeFormData[]>([]);
  const [formData, setFormData] = useState<BadgeFormData | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);  const [editIndex, setEditIndex] = useState<number | null>(null);
  
  const { addNotification } = useNotification();

  // Load badges from API on mount
  useEffect(() => {
    loadBadges();
  }, []);
  const loadBadges = async () => {
    try {
      const badgesFromAPI = await adminBadgeService.getAllBadges();
      setBadges(badgesFromAPI);
    } catch (error) {
      console.error('Error loading badges:', error);
      addNotification('Failed to load badges', 'error');
    }
  };

  const handleEditBadge = (index: number) => {
    setFormData(badges[index]);
    setIsEditing(true);
    setEditIndex(index);
    setShowForm(true);
    setBadgeTab('create');
  };
  const handleDeleteBadge = async (index: number) => {
    try {
      const badge = badges[index];
      if (badge.id) {
        await adminBadgeService.deleteBadge(badge.id);
      }
      setBadges(badges.filter((_, i) => i !== index));
      addNotification('Badge deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting badge:', error);
      addNotification('Failed to delete badge', 'error');
    }
  };

  return (
    <div className="space-y-8">
      {/* Admin Tools Section */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              className={`flex-1 py-4 px-6 text-center font-medium ${
                activeTool === 'badges'
                  ? 'text-indigo-600 border-b-2 border-indigo-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTool('badges')}
            >
              Badge Builder
            </button>
            <button
              className={`flex-1 py-4 px-6 text-center font-medium ${
                activeTool === 'coupons'
                  ? 'text-indigo-600 border-b-2 border-indigo-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTool('coupons')}
            >
              Coupon Generator
            </button>
            <button
              className={`flex-1 py-4 px-6 text-center font-medium ${
                activeTool === 'bulk-award'
                  ? 'text-indigo-600 border-b-2 border-indigo-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTool('bulk-award')}
            >
              Bulk Badge Award
            </button>
            <button
              className={`flex-1 py-4 px-6 text-center font-medium ${
                activeTool === 'dev-tools'
                  ? 'text-indigo-600 border-b-2 border-indigo-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTool('dev-tools')}
            >
              Dev Tools
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTool === 'badges' && (
            <div>
              <div className="flex border-b mb-4">
                <button
                  onClick={() => setBadgeTab('create')}
                  className={`py-2 px-4 font-medium focus:outline-none ${badgeTab === 'create' ? 'text-indigo-600 border-b-2 border-indigo-500' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Create Badge
                </button>
                <button
                  onClick={() => setBadgeTab('manage')}
                  className={`py-2 px-4 font-medium focus:outline-none ${badgeTab === 'manage' ? 'text-indigo-600 border-b-2 border-indigo-500' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Manage Badges
                </button>
              </div>
              {badgeTab === 'create' && <BadgeBuilder badges={badges} setBadges={setBadges} formData={formData} setFormData={setFormData} showForm={showForm} setShowForm={setShowForm} isEditing={isEditing} setIsEditing={setIsEditing} editIndex={editIndex} setEditIndex={setEditIndex} />}
              {badgeTab === 'manage' && <BadgeCrudManager badges={badges} onEdit={handleEditBadge} onDelete={handleDeleteBadge} />}
            </div>
          )}
          {activeTool === 'coupons' && <CouponGenerator />}
          {activeTool === 'bulk-award' && <BulkBadgeAward />}
          {activeTool === 'dev-tools' && <DevTools />}
        </div>
      </div>
    </div>
  );
};

export default AdminToolsPage;